import { fetchCMSData } from "@clients/contentfulClient";
import { RecursiveFAQResult_faqCollection_items } from "@components/FAQList/queries/contentfulTypes/RecursiveFAQResult";
import {
  GET_FAQLIST_BY_CONCEPTS_RESULTS,
  GET_FAQLIST_RESULTS,
} from "@components/FAQList/queries/getListBlockResult.query";
import { GET_RECURSIVE_FAQ_RESULTS } from "@components/FAQList/queries/getRecursiveFAQResult.query";
import {
  FAQListBlockType,
  FAQListBlockType_faqListBlock_manualPostsCollection_items,
} from "@components/FAQListBlock/queries/contentfulTypes/FAQListBlockType";

import { isConceptSchemeAfaseId } from "constants/TaxonomyConcepts";
import { FaqListItemWithConcepts } from "types/faqFilters";

export const getResultForFAQListBlock = async (
  faqListData: FAQListBlockType,
  preview: boolean,
): Promise<Array<FaqListItemWithConcepts>> => {
  const { faqListBlock } = faqListData;
  let uniquePageList = new Array<FaqListItemWithConcepts>();
  const manualPages = faqListBlock?.manualPostsCollection?.items.filter(Boolean);
  const resultCollection: Array<RecursiveFAQResult_faqCollection_items> = await GetManualFAQs(
    manualPages,
    preview,
  );

  const concepts = faqListBlock?.contentfulMetadata?.concepts;
  const hasConcepts = concepts?.length > 0;
  const hasDynamicFaqs = faqListBlock?.includeDynamicFaqs;

  if (hasConcepts || hasDynamicFaqs) {
    let resultItems: Array<FaqListItemWithConcepts> = [];

    if (hasConcepts) {
      const conceptIds = concepts.map((c) => c.id);
      const validConceptIds = conceptIds.filter(isConceptSchemeAfaseId);
      if (validConceptIds.length > 0) {
        resultItems = await GetFAQByConceptsQuery(validConceptIds, preview);
      }
    } else if (hasDynamicFaqs) {
      resultItems = await GetFAQByQuery(faqListBlock?.categories, preview);
    }

    const orderedList = OrderListItems(resultItems, faqListBlock?.sortBy);

    uniquePageList = orderedList.filter(
      (page) => !resultCollection?.some((manualPage) => manualPage?.sys?.id === page?.sys?.id),
    );
  }

  let items = [].concat(resultCollection, uniquePageList) as Array<FaqListItemWithConcepts>;
  if (items?.length > faqListBlock?.numberOfItems)
    items = items.slice(0, faqListBlock?.numberOfItems);
  return items;
};

const GetFAQByQuery = async (
  tags: string[],
  preview: boolean,
): Promise<Array<FaqListItemWithConcepts>> => {
  const faqs: Array<FaqListItemWithConcepts> = [];

  const getAllFAQ = async (skip, tags): Promise<Array<FaqListItemWithConcepts>> => {
    const { data } = await fetchCMSData(GET_FAQLIST_RESULTS(skip, tags, preview), preview);

    for (const i of data?.faqCollection?.items) {
      faqs.push(i);
    }
    if (data?.faqCollection?.total > faqs.length) {
      return await getAllFAQ(faqs.length, tags);
    }
    return faqs;
  };
  const allFaqs = await getAllFAQ(0, tags);
  const faqListResult = allFaqs as Array<FaqListItemWithConcepts>;

  return faqListResult;
};

const GetFAQByConceptsQuery = async (
  concepts: string[],
  preview: boolean,
): Promise<Array<FaqListItemWithConcepts>> => {
  const faqs: Array<FaqListItemWithConcepts> = [];

  const getAllFAQ = async (skip, concepts): Promise<Array<FaqListItemWithConcepts>> => {
    const { data } = await fetchCMSData(
      GET_FAQLIST_BY_CONCEPTS_RESULTS(skip, concepts, preview),
      preview,
    );

    for (const i of data?.faqCollection?.items) {
      faqs.push(i);
    }
    if (data?.faqCollection?.total > faqs.length) {
      return await getAllFAQ(faqs.length, concepts);
    }
    return faqs;
  };
  const allFaqs = await getAllFAQ(0, concepts);
  const faqListResult = allFaqs as Array<FaqListItemWithConcepts>;

  return faqListResult;
};

const GetManualFAQs = async (
  faqCollection: FAQListBlockType_faqListBlock_manualPostsCollection_items[],
  preview: boolean,
): Promise<Array<RecursiveFAQResult_faqCollection_items>> => {
  const resultCollection: Array<RecursiveFAQResult_faqCollection_items> = [];

  const faqIds = faqCollection?.map(
    (faq: FAQListBlockType_faqListBlock_manualPostsCollection_items) => faq?.sys?.id,
  );

  const GetFAQRecursively = async (
    faqCollection: FAQListBlockType_faqListBlock_manualPostsCollection_items[],
    skip: number = 0,
  ): Promise<Array<RecursiveFAQResult_faqCollection_items>> => {
    const faqCollectionCopy = [...faqCollection];

    const ids = faqCollectionCopy?.splice(skip, 10).map((i) => `"${i.sys.id}"`);
    const { data } = await fetchCMSData(GET_RECURSIVE_FAQ_RESULTS(ids.join(","), preview), preview);

    for (const item of data?.faqCollection?.items) {
      resultCollection.push(item);
    }
    if (faqCollection?.length > resultCollection?.length) {
      return await GetFAQRecursively(faqCollection, resultCollection?.length);
    }

    return resultCollection?.sort((a, b) => faqIds?.indexOf(a.sys.id) - faqIds?.indexOf(b.sys.id));
  };
  const allFaqs = await GetFAQRecursively(faqCollection);
  const faqListResult = allFaqs as Array<FaqListItemWithConcepts>;

  return faqListResult as Array<RecursiveFAQResult_faqCollection_items>;
};

const OrderListItems = (
  items: FaqListItemWithConcepts[],
  sortBy: string,
): Array<FaqListItemWithConcepts> => {
  switch (sortBy) {
    case "Alfabetisk":
      return items?.sort((a, b) => a?.question?.localeCompare(b?.question));
    case "Publiceringsdatum":
      return items?.sort(
        (a, b) =>
          new Date(b?.sys?.publishedAt)?.getTime() - new Date(a?.sys?.publishedAt)?.getTime(),
      );
    case "Skapandedatum":
      return items?.sort(
        (a, b) =>
          new Date(b?.sys?.firstPublishedAt)?.getTime() -
          new Date(a?.sys?.firstPublishedAt)?.getTime(),
      );
  }
};
